/**
 * Dashboard absences : KPI globaux + top absentéistes + courbe + par classe.
 */
import React, { useMemo, useState } from 'react';
import {
  BarChart3, CheckCircle2, XCircle, Clock, FileCheck, AlertTriangle, TrendingUp, Users,
  User as UserIcon, Search,
} from 'lucide-react';
import { useDashboardAbsencesQuery } from '../../services/vieScolaireApi';
import { useGetClassesQuery, useGetPolesQuery, useGetOptionsQuery } from '../../services/polesOptionsApi';
import { useGetEtudiantsQuery } from '../../services/etudiantsApi';
import FicheEtudiantModal from '../../components/FicheEtudiant/FicheEtudiantModal';

function isoJour(d: Date): string { return d.toISOString().slice(0, 10); }

const STATUTS: { k: 'present'|'retard'|'absent'|'justifie'|'incident'; label: string; couleur: string; Icon: any }[] = [
  { k: 'present',  label: 'Présent',   couleur: 'text-green-500',  Icon: CheckCircle2 },
  { k: 'retard',   label: 'Retard',    couleur: 'text-amber-500',  Icon: Clock },
  { k: 'absent',   label: 'Absent',    couleur: 'text-red-500',    Icon: XCircle },
  { k: 'justifie', label: 'Justifiée', couleur: 'text-blue-400',   Icon: FileCheck },
  { k: 'incident', label: 'Incident',  couleur: 'text-orange-500', Icon: AlertTriangle },
];

const NIVEAUX = ['L1', 'L2', 'L3', 'M1', 'M2'];

export default function DashboardAbsences() {
  const ya30 = new Date(Date.now() - 30 * 86_400_000);
  const today = new Date();
  const [depuis, setDepuis] = useState(isoJour(ya30));
  const [jusqua, setJusqua] = useState(isoJour(today));
  const [pole, setPole] = useState('');
  const [option, setOption] = useState('');
  const [niveau, setNiveau] = useState('');
  const [classe, setClasse] = useState('');
  const [semestre, setSemestre] = useState<'' | 'impair' | 'pair'>('');
  const [matriculeFiche, setMatriculeFiche] = useState<string | null>(null);
  const [matriculeRecherche, setMatriculeRecherche] = useState('');
  const [rechercheEtudiant, setRechercheEtudiant] = useState('');

  const { data: classes } = useGetClassesQuery({ actif: true });
  const { data: poles } = useGetPolesQuery({ actif: true });
  const { data: options } = useGetOptionsQuery({ actif: true });

  const polesArr = (poles || []) as any[];
  const optionsArr = (options || []) as any[];
  const optionsFiltrees = useMemo(() => {
    if (!pole) return optionsArr;
    const p = polesArr.find((x) => x.nom === pole || x.code === pole);
    if (!p) return optionsArr;
    return optionsArr.filter((o: any) => o.pole === p.id);
  }, [optionsArr, polesArr, pole]);

  const aDesFiltres = !!(pole || option || niveau || classe);

  const { data: etudiantsList, isFetching: etudiantsLoading } = useGetEtudiantsQuery(
    {
      pole: pole || undefined,
      option: option || undefined,
      niveau: niveau || undefined,
      classe: classe || undefined,
      actif: true,
    },
    { skip: !aDesFiltres },
  );

  const etudiantsFiltres = useMemo(() => {
    const all = (etudiantsList || []) as any[];
    const q = rechercheEtudiant.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (e) =>
        (e.matricule || '').toLowerCase().includes(q) ||
        (e.nom || '').toLowerCase().includes(q) ||
        (e.prenoms || '').toLowerCase().includes(q)
    );
  }, [etudiantsList, rechercheEtudiant]);

  const { data, isFetching } = useDashboardAbsencesQuery({
    depuis, jusqua,
    classe: classe || undefined,
    niveau: niveau || undefined,
    pole: pole || undefined,
    option: option || undefined,
    semestre: semestre || undefined,
  });

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <BarChart3 size={24} /> Dashboard absences
        </h1>
        <div className="flex gap-2 items-center flex-wrap">
          <input type="date" value={depuis} onChange={(e) => setDepuis(e.target.value)}
            className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" />
          <span className="text-xs text-gray-500">→</span>
          <input type="date" value={jusqua} onChange={(e) => setJusqua(e.target.value)}
            className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" />
          <select value={pole}
            onChange={(e) => { setPole(e.target.value); setOption(''); }}
            className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
            <option value="">Tous pôles</option>
            {polesArr.map((p: any) => <option key={p.id} value={p.nom}>{p.nom}</option>)}
          </select>
          <select value={option} onChange={(e) => setOption(e.target.value)}
            className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
            <option value="">Toutes options</option>
            {optionsFiltrees.map((o: any) => <option key={o.id} value={o.nom}>{o.nom}</option>)}
          </select>
          <select value={niveau} onChange={(e) => setNiveau(e.target.value)}
            className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
            <option value="">Tous niveaux</option>
            {NIVEAUX.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <select value={semestre} onChange={(e) => setSemestre(e.target.value as any)}
            className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            title="Filtrer par semestre académique (basé sur le mois de la séance)">
            <option value="">Tous semestres</option>
            <option value="impair">Impair (S1/S3/S5 · août–janv.)</option>
            <option value="pair">Pair (S2/S4/S6 · févr.–juil.)</option>
          </select>
          <select value={classe} onChange={(e) => setClasse(e.target.value)}
            className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
            <option value="">Toutes classes</option>
            {(classes || []).map((c: any) => <option key={c.code} value={c.code}>{c.code}</option>)}
          </select>
          <form onSubmit={(e) => {
              e.preventDefault();
              const m = matriculeRecherche.trim().toUpperCase();
              if (m) setMatriculeFiche(m);
            }}
            className="flex items-center gap-1">
            <input type="text" value={matriculeRecherche}
              onChange={(e) => setMatriculeRecherche(e.target.value)}
              placeholder="Matricule…"
              className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 w-28 uppercase" />
            <button type="submit"
              className="px-2 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded font-medium">
              Fiche
            </button>
          </form>
        </div>
      </div>

      {/* Panneau liste étudiants — visible quand au moins un filtre actif */}
      {aDesFiltres && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="text-sm font-bold text-gray-700 dark:text-gray-300 flex items-center gap-2">
              <Users size={16} /> Étudiants {pole && `· ${pole}`}{option && ` · ${option}`}{niveau && ` · ${niveau}`}{classe && ` · ${classe}`}
              <span className="text-xs text-gray-400 dark:text-gray-500 font-normal">
                ({etudiantsFiltres.length}{rechercheEtudiant && ` / ${etudiantsList?.length || 0}`})
              </span>
            </h2>
            <div className="relative">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" value={rechercheEtudiant}
                onChange={(e) => setRechercheEtudiant(e.target.value)}
                placeholder="Filtrer (nom, matricule…)"
                className="pl-7 pr-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 w-56" />
            </div>
          </div>

          {etudiantsLoading ? (
            <div className="text-center text-gray-500 text-sm py-6">Chargement des étudiants…</div>
          ) : etudiantsFiltres.length === 0 ? (
            <div className="text-center text-gray-500 text-sm py-6">Aucun étudiant ne correspond aux filtres</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 max-h-96 overflow-y-auto pr-1">
              {etudiantsFiltres.map((e: any) => (
                <button key={e.id || e.matricule}
                  onClick={() => setMatriculeFiche(e.matricule)}
                  className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50/40 dark:hover:bg-blue-900/10 text-left transition-all">
                  <div className="w-8 h-8 flex-shrink-0 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden flex items-center justify-center">
                    {e.photo_url ? (
                      <img src={e.photo_url} alt={e.nom} className="w-full h-full object-cover" />
                    ) : (
                      <UserIcon size={14} className="text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-gray-900 dark:text-white truncate">
                      {e.nom} {e.prenoms}
                    </div>
                    <div className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                      <span className="font-mono">{e.matricule}</span>
                      {e.classe && ` · ${e.classe}`}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {isFetching && !data && (
        <div className="text-center text-gray-500 py-12">Chargement…</div>
      )}

      {data && data.kpi.total_pointages === 0 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-8 text-center">
          <BarChart3 size={36} strokeWidth={1.3} className="mx-auto mb-3 text-gray-400 dark:text-gray-600" />
          <div className="text-base font-semibold text-gray-700 dark:text-gray-300">Aucun appel validé sur la période</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1.5 max-w-md mx-auto">
            Le dashboard se remplira automatiquement dès que les surveillants
            commenceront à valider les appels (page « Surveillance & Présence »).
          </div>
        </div>
      )}

      {data && data.kpi.total_pointages > 0 && (
        <>
          {/* KPI principal */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 mb-4 text-center">
            <div className="text-5xl font-black text-green-600 dark:text-green-400">
              {data.kpi.taux_presence.toFixed(1)}%
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 uppercase tracking-wide font-semibold">
              Taux de présence sur la période
            </div>
            <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              {data.kpi.total_pointages} pointages
              {data.kpi.total_pointages < 20 && (
                <span className="ml-2 inline-block px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-[10px] rounded">
                  Échantillon réduit — peu représentatif
                </span>
              )}
            </div>
          </div>

          {/* 5 KPI statuts */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            {STATUTS.map(s => (
              <div key={s.k} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3 text-center">
                <s.Icon size={20} className={`${s.couleur} mx-auto mb-1`} />
                <div className="text-2xl font-bold text-gray-900 dark:text-white">{data.kpi[s.k]}</div>
                <div className="text-[10px] uppercase text-gray-500 tracking-wide">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Courbe hebdomadaire */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 mb-6">
            <h2 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
              <TrendingUp size={16} /> Évolution hebdomadaire
            </h2>
            {data.semaines.length === 0 ? (
              <div className="text-center text-gray-500 text-sm py-4">Aucune donnée</div>
            ) : (
              <Courbe semaines={data.semaines} />
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Top absentéistes */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
              <h2 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <Users size={16} /> Top 15 absentéistes
              </h2>
              {data.top_absents.length === 0 ? (
                <div className="text-center text-gray-500 text-sm py-4">Personne</div>
              ) : (
                <div className="space-y-1.5">
                  {data.top_absents.map(t => (
                    <div key={t.matricule} className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <button
                        onClick={() => setMatriculeFiche(t.matricule)}
                        className="font-mono text-xs text-blue-600 dark:text-blue-400 hover:underline font-semibold"
                        title="Voir la fiche complète">
                        {t.matricule}
                      </button>
                      <div className="flex-1 flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded overflow-hidden">
                          <div className="h-full bg-red-500" style={{ width: `${Math.min(100, t.taux_absence)}%` }} />
                        </div>
                        <span className="text-xs font-bold text-red-500 w-12 text-right">{t.taux_absence}%</span>
                      </div>
                      <span className="text-[11px] text-gray-500 w-16 text-right">
                        {t.nb_absences} / {t.total_seances}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Par classe */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
              <h2 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">Par classe</h2>
              {data.par_classe.length === 0 ? (
                <div className="text-center text-gray-500 text-sm py-4">
                  {classe ? 'Filtré sur 1 classe — pas de répartition' : 'Aucune donnée'}
                </div>
              ) : (
                <div className="space-y-1.5">
                  {data.par_classe.slice().sort((a, b) => b.taux_presence - a.taux_presence).map(c => (
                    <div key={c.classe} className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <div className="font-mono text-xs text-gray-900 dark:text-white w-16">{c.classe}</div>
                      <div className="flex-1 flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded overflow-hidden">
                          <div className={`h-full ${c.taux_presence >= 90 ? 'bg-green-500' : c.taux_presence >= 75 ? 'bg-amber-500' : 'bg-red-500'}`}
                            style={{ width: `${c.taux_presence}%` }} />
                        </div>
                        <span className={`text-xs font-bold w-12 text-right ${
                          c.taux_presence >= 90 ? 'text-green-500' : c.taux_presence >= 75 ? 'text-amber-500' : 'text-red-500'
                        }`}>{c.taux_presence}%</span>
                      </div>
                      <span className="text-[11px] text-gray-500 w-12 text-right">{c.total}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {matriculeFiche && (
        <FicheEtudiantModal matricule={matriculeFiche} onClose={() => setMatriculeFiche(null)} />
      )}
    </div>
  );
}

function Courbe({ semaines }: { semaines: { debut: string; taux: number; total: number }[] }) {
  const W = 800; const H = 180; const padX = 30; const padY = 16;
  const innerW = W - padX * 2; const innerH = H - padY * 2;
  if (semaines.length === 0) return null;

  const points = semaines.map((s, i) => ({
    x: padX + (semaines.length > 1 ? (i * innerW) / (semaines.length - 1) : innerW / 2),
    y: padY + innerH - (s.taux / 100) * innerH,
    ...s,
  }));
  const path = points.map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`)).join(' ');
  const aire = path + ` L ${points[points.length - 1].x} ${padY + innerH} L ${padX} ${padY + innerH} Z`;

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H + 24}`} className="block">
      {[25, 50, 75, 100].map(y => {
        const yy = padY + innerH - (y / 100) * innerH;
        return (
          <g key={y}>
            <line x1={padX} x2={W - padX} y1={yy} y2={yy} stroke="currentColor" className="text-gray-200 dark:text-gray-700" strokeDasharray="2 3" strokeWidth={0.5} />
            <text x={4} y={yy + 3} fill="currentColor" className="text-gray-500" fontSize="10">{y}%</text>
          </g>
        );
      })}
      <path d={aire} fill="rgba(37,99,235,0.15)" />
      <path d={path} fill="none" stroke="#2563eb" strokeWidth={1.8} strokeLinejoin="round" strokeLinecap="round" />
      {points.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r={3} fill="#2563eb" />
          <text x={p.x} y={H + 14} textAnchor="middle" fontSize="10" fill="currentColor" className="text-gray-500">
            {new Date(p.debut).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
          </text>
        </g>
      ))}
    </svg>
  );
}
